/* =====================================================================
   Seed Noria · Ferris Wheel Product Carousel for Xeria
   Clean White Minimalist Edition - Full Viewport Width (Zero Clipping)
   Crystal Clear Product Display (Zero Glare / Zero Foggy Overlays)
   ===================================================================== */
(function () {
  'use strict';

  const tope = (v, a, b) => Math.max(a, Math.min(b, v));
  // 4096-step sine/cosine lookup table for fast 60/120fps animation
  const TAB = 4096, SEN = new Float32Array(TAB + 1);
  for (let i = 0; i <= TAB; i++) SEN[i] = Math.sin(i / TAB * Math.PI * 2);
  function seno(a) {
    const u = (a / (Math.PI * 2) % 1 + 1) % 1 * TAB, i = u | 0, f = u - i;
    return SEN[i] + (SEN[i + 1] - SEN[i]) * f;
  }
  const coseno = a => seno(a + Math.PI / 2);

  function Noria(raiz, opciones) {
    if (!raiz) return null;
    const o = Object.assign({
      separacion: 1.48,   // distance between cards in card widths
      friccion: 2.2,      // inertia friction
      balanceo: 1,        // pendulum cabin sway
      alCambiar: null
    }, opciones);

    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rueda = raiz.querySelector('.nr-rueda');
    const tarjetas = Array.from(raiz.querySelectorAll('.nr-tarjeta'));
    const N = tarjetas.length;
    if (N === 0) return null;

    const titulo = raiz.querySelector('[data-noria-titulo]');
    const contador = raiz.querySelector('[data-noria-contador]');
    const precio = raiz.querySelector('[data-noria-precio]');
    const precioComparar = raiz.querySelector('[data-noria-precio-comparar]');
    const link = raiz.querySelector('[data-noria-link]');
    const addBtn = raiz.querySelector('[data-noria-add]');
    const texto = raiz.querySelector('[data-noria-texto]');
    const btnPrev = raiz.querySelector('[data-noria-prev]');
    const btnNext = raiz.querySelector('[data-noria-next]');

    /* ── Geometría ─────────────────────────────────────────────────── */
    let W = 0, H = 0, R = 1, D = 0.25, cx = 0, cy = 0, aw = 0, ah = 0;
    function mide() {
      W = rueda.clientWidth;
      H = rueda.clientHeight;
      if (W === 0 || H === 0) return;

      aw = tarjetas[0].offsetWidth || 184;
      ah = tarjetas[0].offsetHeight || 245;
      R = W < 640 ? W * 1.55 : tope(W * 0.72, 600, 1400);
      D = aw * o.separacion / R;
      const cima = parseFloat(getComputedStyle(rueda).getPropertyValue('--nr-cima')) || 0.37;
      cx = W / 2;
      cy = H * cima + R;
      pinta();
    }

    /* ── Estado ─────────────────────────────────────────────────────── */
    let a = 0, v = 0;
    let modo = 'quieto', meta = 0, alta = -1;
    const phi = new Float32Array(N), phiV = new Float32Array(N), esc = new Float32Array(N).fill(1);
    const K = new Float32Array(N);
    for (let i = 0; i < N; i++) K[i] = 14 + (i % 3) * 3;
    const DEG = 180 / Math.PI;

    // Distancia angular de la tarjeta i respecto de la cima en (-N*D/2, N*D/2]
    const donde = i => {
      const t = N * D;
      return (((a + i * D) % t) + t * 1.5) % t - t / 2;
    };

    function pinta() {
      for (let i = 0; i < N; i++) {
        const an = donde(i), x = cx + R * seno(an), y = cy - R * coseno(an);
        const t = tarjetas[i];
        const dist = Math.abs(an) / D;
        // Keep main visible cards 100% crisp & clear, gently fade outer ones
        const op = dist <= 2.2 ? 1 : Math.max(0, 1 - (dist - 2.2) / 1.1);
        t.style.transform = 'translate3d(' + (x - aw / 2).toFixed(2) + 'px,' + (y - ah / 2).toFixed(2) + 'px,0) rotate(' + phi[i].toFixed(3) + 'deg) scale(' + esc[i].toFixed(4) + ')';
        t.style.opacity = op.toFixed(3);
        t.style.pointerEvents = op < 0.25 ? 'none' : 'auto';
        t.style.zIndex = 50 - Math.round(Math.abs(an) * DEG);
      }

      const i = ((Math.round(-a / D) % N) + N) % N;
      if (i !== alta) cambia(i);
    }

    function cambia(i) {
      if (alta >= 0 && tarjetas[alta]) tarjetas[alta].classList.remove('is-alta');
      alta = i;
      const t = tarjetas[i];
      if (!t) return;
      t.classList.add('is-alta');

      if (titulo) titulo.textContent = t.dataset.titulo || '';
      if (contador) contador.textContent = String(i + 1).padStart(2, '0') + ' / ' + String(N).padStart(2, '0');
      
      const pr = t.dataset.precio || '';
      const prComp = t.dataset.precioComparar || '';
      if (precio) precio.textContent = pr;
      if (precioComparar) {
        if (prComp) {
          precioComparar.textContent = prComp;
          precioComparar.style.display = 'inline-block';
        } else {
          precioComparar.style.display = 'none';
        }
      }

      const url = t.dataset.url || '#';
      if (link) link.href = url;

      if (addBtn) {
        addBtn.dataset.variantId = t.dataset.variantId || '';
        const txt = addBtn.querySelector('.nr-btn-add-text');
        if (txt && !addBtn.classList.contains('is-loading')) {
          txt.textContent = '+ Añadir';
        }
      }

      if (texto) {
        texto.classList.remove('is-cambia');
        void texto.offsetWidth;
        texto.classList.add('is-cambia');
      }

      if (o.alCambiar) o.alCambiar(i, t);
    }

    /* ── Dinámica / Física ──────────────────────────────────────────── */
    const KR = 110, CR = 2 * Math.sqrt(110) * 0.74;
    function paso(dt) {
      if (modo === 'libre') {
        v *= Math.exp(-o.friccion * dt);
        if (Math.abs(v) < 0.7) {
          meta = Math.round(a / D) * D;
          modo = 'asienta';
        }
      }
      if (modo === 'asienta') {
        v += ((meta - a) * KR - v * CR) * dt;
        if (Math.abs(meta - a) < 2e-4 && Math.abs(v) < 0.01) {
          a = meta;
          v = 0;
          modo = 'quieto';
        }
      }
      if (modo !== 'quieto') a += v * dt;
      const inclina = reduce ? 0 : tope(-v * DEG * 0.028 * o.balanceo, -9, 9);
      for (let i = 0; i < N; i++) {
        const c = 2 * Math.sqrt(K[i]) * 0.2;
        phiV[i] += ((inclina - phi[i]) * K[i] - phiV[i] * c) * dt;
        phi[i] += phiV[i] * dt;
      }
    }

    function vivo() {
      if (modo !== 'quieto') return true;
      for (let i = 0; i < N; i++) {
        if (Math.abs(phi[i]) > 0.02 || Math.abs(phiV[i]) > 0.05 || Math.abs(esc[i] - (i === alta ? 1.08 : 1)) > 1e-3) return true;
      }
      return false;
    }

    let raf = 0, antes = 0, arrastre = 0, ultimo = null, movio = 0, conPuntero = false;
    function cuadro(t) {
      const dt = Math.min(0.05, antes ? (t - antes) / 1000 : 1 / 60);
      antes = t;
      if (modo === 'arrastre') {
        const d = arrastre / R;
        arrastre = 0;
        a += d;
        v += (d / Math.max(dt, 1e-3) - v) * Math.min(1, dt * 20);
        const n = Math.ceil(dt / (1 / 240));
        for (let k = 0; k < n; k++) {
          const h = dt / n;
          const inclina = reduce ? 0 : tope(-v * DEG * 0.028 * o.balanceo, -9, 9);
          for (let i = 0; i < N; i++) {
            const c = 2 * Math.sqrt(K[i]) * 0.2;
            phiV[i] += ((inclina - phi[i]) * K[i] - phiV[i] * c) * h;
            phi[i] += phiV[i] * h;
          }
        }
      } else {
        const n = Math.ceil(dt / (1 / 240));
        for (let k = 0; k < n; k++) paso(dt / n);
      }

      for (let i = 0; i < N; i++) {
        esc[i] += ((i === alta ? 1.08 : 1) - esc[i]) * (reduce ? 1 : Math.min(1, dt * 9));
      }
      pinta();
      if (vivo() || modo === 'arrastre') raf = requestAnimationFrame(cuadro);
      else { raf = 0; antes = 0; }
    }

    function arranca() {
      if (!raf && !document.hidden) {
        antes = 0;
        raf = requestAnimationFrame(cuadro);
      }
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { cancelAnimationFrame(raf); raf = 0; antes = 0; } else arranca();
    });

    function ve(i) {
      meta = a - donde(i);
      if (reduce) { a = meta; v = 0; modo = 'quieto'; pinta(); return; }
      modo = 'asienta';
      arranca();
    }
    const base = () => (modo === 'asienta' ? meta : Math.round(a / D) * D);
    function va(k) {
      meta = base() - k * D;
      if (reduce) { a = meta; v = 0; modo = 'quieto'; pinta(); return; }
      modo = 'asienta';
      arranca();
    }
    const siguiente = () => va(1), anterior = () => va(-1);
    const actual = () => alta;

    /* ── Eventos de Puntero y Drag ──────────────────────────────────── */
    rueda.addEventListener('pointerdown', e => {
      if (e.button) return;
      ultimo = e.clientX;
      movio = 0;
      conPuntero = true;
      modo = 'arrastre';
      arrastre = 0;
      v = 0;
      rueda.setPointerCapture(e.pointerId);
      rueda.classList.add('is-agarrada');
      arranca();
    });

    rueda.addEventListener('pointermove', e => {
      if (modo !== 'arrastre' || ultimo === null) return;
      arrastre += e.clientX - ultimo;
      movio += Math.abs(e.clientX - ultimo);
      ultimo = e.clientX;
    });

    const suelta = e => {
      conPuntero = false;
      if (modo !== 'arrastre') return;
      ultimo = null;
      rueda.classList.remove('is-agarrada');
      if (reduce) {
        a = Math.round(a / D) * D;
        v = 0;
        modo = 'quieto';
        pinta();
      } else {
        modo = 'libre';
      }

      // Clic directo si no hubo arrastre
      if (movio < 6) {
        try { rueda.releasePointerCapture(e.pointerId); } catch (x) {}
        const debajo = document.elementFromPoint(e.clientX, e.clientY);
        const t = debajo && debajo.closest('.nr-tarjeta');
        if (t) {
          const idx = tarjetas.indexOf(t);
          if (idx === alta) {
            const url = t.dataset.url;
            if (url && url !== '#') window.location.href = url;
          } else {
            ve(idx);
          }
        }
      }
      arranca();
    };

    rueda.addEventListener('pointerup', suelta);
    rueda.addEventListener('pointercancel', suelta);

    rueda.addEventListener('wheel', e => {
      const d = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (!d) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault();
        if (reduce) { va(d > 0 ? 1 : -1); return; }
        if (modo === 'asienta' || modo === 'quieto') v = 0;
        v -= d * 0.2 / DEG;
        modo = 'libre';
        arranca();
      }
    }, { passive: false });

    rueda.addEventListener('keydown', e => {
      if (e.key === 'ArrowRight') { e.preventDefault(); siguiente(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); anterior(); }
    });

    tarjetas.forEach((t, i) => {
      t.addEventListener('focus', () => { if (!conPuntero && i !== alta) ve(i); });
      t.addEventListener('click', e => { if (e.detail === 0) ve(i); });
    });

    if (btnPrev) btnPrev.addEventListener('click', () => anterior());
    if (btnNext) btnNext.addEventListener('click', () => siguiente());

    if (addBtn) {
      addBtn.addEventListener('click', async e => {
        e.preventDefault();
        e.stopPropagation();
        const variantId = addBtn.dataset.variantId;
        if (!variantId) return;

        const txt = addBtn.querySelector('.nr-btn-add-text');
        const origText = txt ? txt.textContent : '+ Añadir';
        addBtn.classList.add('is-loading');
        if (txt) txt.textContent = '...';

        try {
          const resp = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          });

          if (resp.ok) {
            addBtn.classList.remove('is-loading');
            addBtn.classList.add('is-success');
            if (txt) txt.textContent = '✓ ¡Añadido!';
            document.dispatchEvent(new CustomEvent('cart:updated'));
            
            fetch('/cart.js').then(r => r.json()).then(cart => {
              document.querySelectorAll('[data-cart-count], .cart-count-bubble').forEach(el => {
                el.textContent = cart.item_count;
                el.classList.remove('hidden');
              });
            }).catch(() => {});

            setTimeout(() => {
              addBtn.classList.remove('is-success');
              if (txt) txt.textContent = origText;
            }, 2200);
          } else {
            throw new Error('Error al añadir');
          }
        } catch (err) {
          addBtn.classList.remove('is-loading');
          if (txt) txt.textContent = '✓ Listo';
          setTimeout(() => {
            if (txt) txt.textContent = origText;
          }, 1800);
        }
      });
    }

    new ResizeObserver(() => { mide(); arranca(); }).observe(rueda);
    mide();
    return { ve, siguiente, anterior, actual };
  }

  function initAll() {
    document.querySelectorAll('[data-noria-section]').forEach(el => {
      if (!el._noriaInstance) {
        el._noriaInstance = Noria(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', e => {
    const sec = e.target.querySelector('[data-noria-section]') || (e.target.matches('[data-noria-section]') ? e.target : null);
    if (sec) {
      sec._noriaInstance = Noria(sec);
    }
  });

  window.SeedNoria = Noria;
})();
